import React, { useContext, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import produce from 'immer';

import './FilterModal.scss';
import { UpdatePageModel } from '../UpdatePageModel';
import { BulkDataTextarea, DatePicker, ErrorMessage, FormInput, IBulkDataTextareaAPI, SearchDropdown, SearchItem } from 'components';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader} from '@cfa/react-components';
import { AsyncStatus, DynamoColumn, FilterType, LocationAttr } from 'enums';
import { IFilter, ILocationGroup } from 'interfaces';
import { FiltersService, ISearcherOnSearchResponse, LocationGroupsService, LocationsService, parseAxiosError, UsersService } from 'services';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useLuxonTimezone } from 'hooks';
import { applyTimezone, unapplyTimezone } from 'services/Utils';
import { FilterMenu } from './FilterMenu';
import { BoardContext } from '../Update';

interface IDevicesPerLocationForm {
    devicesPerLocation: string,
    minDevicesPerLocation: string,
}

interface IMenuVersionForm {
    menuKey: string,
    menuValue: string,
}

interface IFirmwareVersionForm {
    firmwareKey: string,
    firmwareValue: string,
}

interface IPercentageForm {
    percentage: string,
}

interface IFilterModalProps {
    canEdit: boolean,
    filter: IFilter,
    isGlobal: boolean,
    isNew: boolean,
    model: UpdatePageModel,
    show: boolean,
    stageId: number,
    stageIndex: number,
    onHide: () => void,
}

interface IFilterModalState {
    loadError?: string,
    loadStatus: AsyncStatus,
    allLocationAttrVals: {[attr: string]: string[]},

    saveError?: string,
    saveStatus: AsyncStatus,
    saveType?: 'save' | 'delete',

    defaultLocations: string[],
    defaultSerials: string[],
    defaultThingIDs: string[],
    filterType: FilterType,
    lastSeenOperator: '>' | '<',
    lastSeenTime: Date,
    locationAttr: LocationAttr,
    locationAttrVals: {[value: string]: boolean},

    menuKey?: string,
    menuValue?: string,

    firmwareKey?: string,
    firmwareValue?: string,

    showPercentageExample?: boolean,
}

export function FilterModal({ canEdit, filter, isGlobal, isNew, model, show, stageId, stageIndex, onHide }: IFilterModalProps) {
    const { t, i18n } = useTranslation();
    const [state, setState] = useState<IFilterModalState>(() => {
        // Copy the filter props so we don't modify the original.
        // By breaking up the filter into all props for all filter types,
        // we can keep props and avoid clearing them. For example, 
        // if the user changes the type to "Serials", then back to "Locations", we won't
        // have cleared the locations property.
        
        // The API uses an array for the values, but we're using a map for fast lookups when checking if something is selected.
        const vals = {};
        if (filter.locationAttrValues) {
            for (const val of filter?.locationAttrValues) {
                vals[val] = true;
            }
        }

        // It looks nicer if we initialize this to midnight instead of "now".
        const defaultLastSeenTime = new Date();
        defaultLastSeenTime.setHours(0, 0, 0, 0);

        return {
            allLocationAttrVals: {},
            defaultLocations: (filter.locations || []).slice(),
            defaultSerials: (filter.serialNumbers || []).slice(),
            defaultThingIDs: (filter.thingIDs || []).slice(),
            filterType: filter.type,
            lastSeenOperator: filter.lastSeenOperator || '>',
            lastSeenTime: filter.lastSeenTime || defaultLastSeenTime,
            loadStatus: AsyncStatus.Loading,
            locationAttr: filter.locationAttr || LocationAttr.LocationStatus,
            locationAttrVals: vals,
            saveStatus: AsyncStatus.Initial,
        };
    });
    const {
        allLocationAttrVals,
        defaultLocations,
        defaultSerials,
        defaultThingIDs,
        filterType,
        lastSeenOperator,
        lastSeenTime,
        locationAttr,
        locationAttrVals,
        saveError,
        saveStatus,
        saveType,
        showPercentageExample,
    } = state;
    const { timezone, hasTimezone } = useLuxonTimezone(UsersService.me.timezone);
    const boardContext = useContext(BoardContext);

    const formDevicesPerLoc = useForm<IDevicesPerLocationForm>({
        mode: 'onTouched',
        defaultValues: {
            devicesPerLocation: (filter.numberOfDevicesPerLocationToDeploy || 1) + '',
            minDevicesPerLocation: (filter.minNumberOfDevicesPerLocation || 2) + '',
        }
    });
    const formMenuVersion = useForm<IMenuVersionForm>({
        mode: 'onTouched',
        defaultValues: {
            menuKey: filter.menuKey || 'current',
            menuValue: filter.menuValue,
        }
    });
    const formFirmwareVersion = useForm<IFirmwareVersionForm>({
        mode: 'onTouched',
        defaultValues: {
            firmwareKey: filter.firmwareKey || '',
            firmwareValue: filter.firmwareValue,
        }
    });
    const formPercentage = useForm<IPercentageForm>({
        mode: 'onTouched',
        defaultValues: {
            percentage: typeof filter.percentageOfDevices === 'number'
                ? (filter.percentageOfDevices + '%') 
                : '10%',
        }
    });

    // We're using uncontrolled textareas so we don't have to rerender on every keypress
    const locationsTextareaRef = useRef<IBulkDataTextareaAPI>();
    const serialsTextareaRef = useRef<IBulkDataTextareaAPI>();
    const thingIDsTextareaRef = useRef<IBulkDataTextareaAPI>();

    useEffect(() => {

        // Load location attributes and values
        LocationsService.getAttributes()
            .then((attrs) => {
                setState(produce(draft => {
                    draft.allLocationAttrVals = attrs;
                    draft.loadStatus = AsyncStatus.Success;
                }));
            })
            .catch((err) => {
                setState(produce(draft => {
                    draft.loadError = parseAxiosError(err);
                    draft.loadStatus = AsyncStatus.Error;
                }));
            })
        
        // display warning if filtering on same field as update
        const uType = model.update["update_file_dynamo_attribute_id"];
        const uKey = model.update["update_file_key_matcher"];

        if (uType === DynamoColumn.Firmware && filter.firmwareKey !== "" && filter.firmwareKey === uKey) {
            setState(produce(draft => {
                draft.saveError = "Filter is on the same field being updated, reports will be affected.";
            }))
        } else if (uType === DynamoColumn.Menu && filter.menuKey !== "" && filter.menuKey === uKey) {
            setState(produce(draft => {
                draft.saveError = "Filter is on the same field being updated, reports will be affected.";
            }))
        }
    }, [])

    function onChangeFilterType(e) {
        const newType = Number(e.target.value);
        // Get the values from the textareas so we can save them.
        // We have to do this before calling setState because the textareas also call setState.
        const locationsVal = locationsTextareaRef.current?.getLinesAndValidate();
        const serialsVal = serialsTextareaRef.current?.getLinesAndValidate();
        const thingIDsVal = thingIDsTextareaRef.current?.getLinesAndValidate();
        
        setState(produce(draft => {
            draft.filterType = newType;

            // Save textarea current values
            if (locationsVal) {
                draft.defaultLocations = locationsVal.lines;
            }
            if (serialsVal) {
                draft.defaultSerials = serialsVal.lines;
            }
            if (thingIDsVal) {
                draft.defaultThingIDs = thingIDsVal.lines;
            }
        }));
    }

    function validatePositiveInt(v: string): boolean | string {
        const err = 'Must be a positive integer';
        // Make sure it looks like an integer
        if (!(v + '').match(/^\d+$/)) {
            return err;
        }
        // Parse it and make sure the value is greater than or equal to 1
        if (!(parseInt(v, 10) >= 1)) {
            return err;
        }
        return true;
    }

    function parsePercentVal(v: string): number {
        // Make sure it's a string before we call string methods on it.
        v = v + '';

        // Remove optional percentage sign.
        v = v.replace(/%/g, '');
        
        return parseFloat(v);
    }

    function validatePercent(v: string): boolean | string {
        // Make sure it's a string before we call string methods on it.
        v = v + '';
        const err = 'Must be a number between 0 and 100';

        // See if it looks like a float with an optional % sign.
        // From: https://stackoverflow.com/questions/12643009/regular-expression-for-floating-point-numbers
        if (!v.match(/^([0-9]*[.])?[0-9]+%?$/)) {
            return err;
        }
        
        // See if it's between 0 and 100
        const num = parsePercentVal(v)
        if (isNaN(num) || num < 0 || num > 100) {
            return err;
        }
        return true;
    }

    function togglePercentageExample() {
        setState(produce(draft => {
            draft.showPercentageExample = !draft.showPercentageExample;
        }));
    }

    function onChangeLocationAttr(e) {
        const value = e.target.value;
        setState(produce(draft => {
            draft.locationAttr = value;
            draft.locationAttrVals = {};
        }));
    }

    const isSelectedAllValues = (allLocationAttrVals[locationAttr] || [])
        .every(val => locationAttrVals[val]);

    function toggleSelectAllValues() {
        setState(produce(draft => {
            if (isSelectedAllValues) {
                draft.locationAttrVals = {};
            } else {
                const newVals = {};
                for (const val of Object.values(allLocationAttrVals[locationAttr] || [])) {
                    newVals[val] = true;
                }
                draft.locationAttrVals = newVals;
            }
        }));
    }

    function onToggleLocationAttrVal(val: string) {
        setState(produce(draft => {
            const newVals = {...locationAttrVals};
            newVals[val] = !newVals[val];
            draft.locationAttrVals = newVals;
        }));
    }

    // Apply timezone to lastSeenTime because Ant pickers don't support timezones.
    const lastSeenTimeDt = DateTime.fromJSDate(lastSeenTime, {zone: timezone});
    const lastSeenTimeTimezoned = applyTimezone(lastSeenTimeDt);

    function onChangeLastSeenDate(newDate: Date) {
        // newDate already has the timezone applied, need to unapply
        newDate = unapplyTimezone(newDate, timezone).toJSDate();

        setState(produce(draft => {
            draft.lastSeenTime = newDate;
        }));
    }

    async function onSave() {
        let modifiedFilter: IFilter = {
            type: filterType,
            id: filter.id,
        };
        let isFilterValid = false;

        // Construct a filter from our state vals
        if (filterType === FilterType.Locations) {
            const { lines, isValid } = locationsTextareaRef.current.getLinesAndValidate();
            isFilterValid = isValid;
            modifiedFilter.locations = lines;
        } else if (filterType === FilterType.Serials) {
            const { lines, isValid } = serialsTextareaRef.current.getLinesAndValidate();
            isFilterValid = isValid;
            modifiedFilter.serialNumbers = lines;
        } else if (filterType === FilterType.ThingIDs) {
            const { lines, isValid } = thingIDsTextareaRef.current.getLinesAndValidate();
            isFilterValid = isValid;
            modifiedFilter.thingIDs = lines
        } else if (filterType === FilterType.DevicesPerLocation) {
            await formDevicesPerLoc.handleSubmit((vals) => {
                isFilterValid = true;
                modifiedFilter.minNumberOfDevicesPerLocation = parseInt(vals.minDevicesPerLocation, 10);
                modifiedFilter.numberOfDevicesPerLocationToDeploy = parseInt(vals.devicesPerLocation, 10);
            })();
        } else if (filterType === FilterType.Percentage) {
            await formPercentage.handleSubmit((vals) => {
                isFilterValid = true;
                modifiedFilter.percentageOfDevices = parsePercentVal(vals.percentage);
            })();
        } else if (filterType === FilterType.LocationData) {
            const vals = Object.keys(locationAttrVals);
            isFilterValid = true;
            modifiedFilter.locationAttr = locationAttr;
            modifiedFilter.locationAttrValues = vals;
        } else if (filterType === FilterType.LastSeen) {
            isFilterValid = true;
            modifiedFilter.lastSeenOperator = lastSeenOperator;
            modifiedFilter.lastSeenTime = lastSeenTime;
        } else if (filterType === FilterType.MenuVersion) {
            await formMenuVersion.handleSubmit((vals) => {
                isFilterValid = true;
                modifiedFilter.menuValue = vals.menuValue;
                modifiedFilter.menuKey = vals.menuKey;
            })();
        } else if (filterType === FilterType.FirmwareVersion) {
            await formFirmwareVersion.handleSubmit((vals) => {
                isFilterValid = true;
                modifiedFilter.firmwareValue = vals.firmwareValue;
                modifiedFilter.firmwareKey = vals.firmwareKey;
            })();
        } else {
            setState(produce(draft => {
                draft.saveStatus = AsyncStatus.Error;
                draft.saveError = 'Filter type not implemented yet.';
            }));
            return;
        }

        if (!isFilterValid) {
            return;
        }

        // Save
        setState(produce(draft => {
            draft.saveStatus = AsyncStatus.Loading;
            draft.saveError = '';
            draft.saveType = 'save';
        }))
        let p: Promise<any>;
        if (isNew) {
            p = model.saveNewFilter({
                filter: modifiedFilter,
                isGlobal: isGlobal,
                stageId: stageId,
            });
        } else {
            p = model.saveExistingFilter({
                filter: modifiedFilter,
                isGlobal: isGlobal,
                stageId: stageId,
            })
        }
        p.then(onHide)
            .catch((err) => {
                setState(produce(draft => {
                    draft.saveStatus = AsyncStatus.Error;
                    draft.saveError = model.apiCallError;
                }))
            });
    }

    function onDelete() {
        if (isNew) {
            onHide();
            return;
        }

        setState(produce(draft => {
            draft.saveStatus = AsyncStatus.Loading;
            draft.saveError = '';
            draft.saveType = 'delete';
        }))

        model.deleteFilter({
            filterId: filter.id,
            stageId: stageId,
            isGlobal: isGlobal,
        })
            .then(onHide)
            .catch((err) => {
                setState(produce(draft => {
                    draft.saveStatus = AsyncStatus.Error;
                    draft.saveError = model.apiCallError;
                }))
            });
    }

    function onDuplicate() {
        model.duplicateFilter({
            filterId: filter.id,
            isGlobal: isGlobal,
            stageId: stageId,
        }).catch(() => boardContext.toastError(model.apiCallError));
        onHide();
    }

    if (model.loadStatus !== AsyncStatus.Success) return null;

    return (
        <Modal show={show} onClose={onHide} size="lg" className="filterModal">
            <ModalHeader>
                {isNew && 'New Filter'}
                {!isNew && 'Edit Filter'}
                <FilterMenu
                    canEdit={canEdit}
                    filter={filter}
                    isGlobalFilters={isGlobal}
                    isNew={isNew}
                    model={model}
                    stageIndex={stageIndex}
                    onDelete={onDelete}
                    onDuplicate={onDuplicate}
                />
            </ModalHeader>
            <ModalBody style={{textAlign: "left"}}>
                <div style={{width: '50%'}}>
                    <div className="formItem">
                        <div className="formItem_label">
                            Type
                        </div>
                        <select
                            className="formItem_select"
                            value={filterType}
                            disabled={!canEdit}
                            onChange={onChangeFilterType}
                        >
                            {[
                                FilterType.Locations,
                                FilterType.Serials,
                                FilterType.DevicesPerLocation,
                                FilterType.LocationData,
                                FilterType.LastSeen,
                                FilterType.Percentage,
                                FilterType.MenuVersion,
                                FilterType.FirmwareVersion,
                                FilterType.ThingIDs,
                            ].map((ft) => (
                                <option value={ft} key={ft}>
                                    {t(`filterType.${ft}`)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                {filterType === FilterType.Locations && (<>
                    <p>
                        Only devices at these locations will be included.
                    </p>
                    <div className="formItem">
                        <div className="targetCountsBadge targetCountsBadge_last targetCountsBadge_bottom">
                        <FontAwesomeIcon icon="info-circle" />&nbsp;
                            Please enter a comma or space separated list of locations or select a location group. Each location must be a 5 digit number.
                        </div>
                        <SearchDropdown<ILocationGroup>
                            className="filterModal_locationGroupsSearch"
                            getOptionValue={g => g.name}
                            onChange={g =>  {
                                    locationsTextareaRef.current.setTextarea(g.locations)
                                }
                            }
                            onSearch={(q: string, offset: number): ISearcherOnSearchResponse<ILocationGroup> => {
                                return Promise.resolve({
                                    results: LocationGroupsService.locationGroups.filter(v => v.name.toLowerCase().includes(q.toLowerCase())),
                                   canLoadMore: false, 
                                })
                            }}
                            renderOption={(option) => (
                                <SearchItem label={option.name} />
                            )}
                            style={{width: '350px'}}
                            value={undefined}
                            placeholder="Select a location group"
                        />

                        <BulkDataTextarea 
                            defaultLines={defaultLocations}
                            itemName="location"
                            itemNamePlural="locations"
                            lineValidRegex={FiltersService.regexLocation}
                            readOnly={!canEdit}
                            ref={locationsTextareaRef}
                        />
                    </div>
                </>)}
                {filterType === FilterType.Serials && (<>
                    <p>
                        Only devices with one of these serial numbers will be included.
                    </p>
                    <div className="formItem">
                        <div className="filterModal_textareaLabel">
                            Please enter a comma or space separated list of serial numbers.
                        </div>
                        <BulkDataTextarea
                            defaultLines={defaultSerials}
                            itemName="serial number"
                            itemNamePlural="serial numbers"
                            lineValidRegex={FiltersService.regexSerial}
                            readOnly={!canEdit}
                            ref={serialsTextareaRef}
                        />
                    </div>
                </>)}
                {filterType === FilterType.ThingIDs && (<>
                    <p>
                        Only devices with one of these Thing IDs will be included.
                    </p>
                    <div className="formItem">
                        <div className="filterModal_textareaLabel">
                            Please enter a comma or space separated list of thingIDs.
                        </div>
                        <BulkDataTextarea
                            defaultLines={defaultThingIDs}
                            itemName="thing ID"
                            itemNamePlural="thing IDs"
                            lineValidRegex={FiltersService.regexThingIDs}
                            readOnly={!canEdit}
                            ref={thingIDsTextareaRef}
                        />
                    </div>
                </>)}
                {filterType === FilterType.DevicesPerLocation && (<>
                    <p>
                        During the early stages of a rollout, it's safer to deploy to locations
                        that have more than 1 of a device, so that if the update goes wrong,
                        they have a backup device.
                    </p>
                    <p>
                        For example, if a location has 2 fryers, we should start by deploying to only 1 of those fryers.
                        If a location has less than the minimum number of devices, it will be skipped.
                    </p>
                    <form>
                        <div className="row">
                            <div className="col-6">
                                <FormInput 
                                    autocomplete="off"
                                    disabled={!canEdit}
                                    form={formDevicesPerLoc}
                                    label="Minimum devices per location"
                                    name="minDevicesPerLocation"
                                    registerOptions={{
                                        required: true,
                                        validate: {
                                            positiveInt: validatePositiveInt,
                                        },
                                    }}
                                    type="number"
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-6">
                                <FormInput
                                    autocomplete="off"
                                    disabled={!canEdit}
                                    form={formDevicesPerLoc}
                                    label="Deploy to this many devices per location"
                                    name="devicesPerLocation"
                                    registerOptions={{
                                        required: true,
                                        validate: {
                                            positiveInt: validatePositiveInt,
                                        },
                                    }}
                                    type="number"
                                />
                            </div>
                        </div>
                    </form>
                </>)}
                {filterType === FilterType.Percentage && (<>
                    {isGlobal && (<p>
                        Deploy to a percentage of the devices targeted by global filters.
                        This filter is applied last, after all other global filters.
                    </p>)}
                    {!isGlobal && (<p>
                        Deploy to a percentage of the devices targeted by this stage.
                        This filter is applied last, after global filters and other stage filters.
                    </p>)}

                    <div className="row">
                        <div className="col-6">
                            <FormInput
                                autocomplete="off"
                                disabled={!canEdit}
                                form={formPercentage}
                                label="Percent of total devices"
                                name="percentage"
                                registerOptions={{
                                    required: true,
                                    validate: {
                                        positiveInt: validatePercent,
                                    },
                                }}
                            />
                        </div>
                    </div>
                    <div className="filterModal_exampleRow">
                        <div
                            onClick={togglePercentageExample}
                            className="filterModal_exampleLink"
                        >
                            Show example:&nbsp;
                            {!showPercentageExample && <FontAwesomeIcon icon="caret-right" />}
                            {showPercentageExample && <FontAwesomeIcon icon="caret-down" />}
                        </div>
                        {showPercentageExample && (<div>
                            <p>
                                To deploy an update gradually, you might add a Locations filter in "Global filters".<br/>
                                Then, you can use a percentage filter in each stage to deploy to some of the total devices:
                            </p>
                            <ul>
                                <li>Stage 1: 10% of total</li>
                                <li>Stage 2: 30% of total</li>
                                <li>Stage 3: 100% of total</li>
                            </ul>
                        </div>)}
                    </div>
                </>)}
                {filterType === FilterType.MenuVersion && (<>
                    <p>
                        Only include devices where the menu version matches
                    </p>

                    <form>
                        <div className="row">
                            <div className="col-6">
                                <FormInput 
                                    autocomplete="off"
                                    disabled={!canEdit}
                                    form={formMenuVersion}
                                    label="Menu Key"
                                    name="menuKey"
                                    registerOptions={{
                                        required: true,
                                        validate: (key) => {
                                            const uType = model.update["update_file_dynamo_attribute_id"];
                                            const uKey = model.update["update_file_key_matcher"];
                                            
                                            if ((uType === DynamoColumn.Menu && key === uKey)) {
                                                setState(produce(draft => {
                                                    draft.saveError = "Filter is on the same field being updated, reports will be affected."
                                                }));
                                            } else {
                                                setState(produce(draft => {
                                                    draft.saveError = ""
                                                }));
                                            }
                                            
                                            return true;
                                        }
                                    }}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-6">
                                <FormInput
                                    autocomplete="off"
                                    disabled={!canEdit}
                                    form={formMenuVersion}
                                    label="Menu Version"
                                    name="menuValue"
                                    registerOptions={{
                                        required: true,
                                    }}
                                />
                            </div>
                        </div>
                    </form>
                </>)}
                {filterType === FilterType.FirmwareVersion && (<>
                    <p>
                        Only include devices where the firmware version matches
                    </p>

                    <form>
                        <div className="row">
                            <div className="col-6">
                                <FormInput 
                                    autocomplete="off"
                                    disabled={!canEdit}
                                    form={formFirmwareVersion}
                                    label="Firmware Key"
                                    name="firmwareKey"
                                    registerOptions={{
                                        required: true,
                                        validate: (key) => {
                                            const uType = model.update["update_file_dynamo_attribute_id"];
                                            const uKey = model.update["update_file_key_matcher"];
                                            
                                            if (uType === DynamoColumn.Firmware && key === uKey) {
                                                setState(produce(draft => {
                                                    draft.saveError = "Filter is on the same field being updated, reports will be affected."
                                                }));
                                            } else {
                                                setState(produce(draft => {
                                                    draft.saveError = ""
                                                }));
                                            }
                                            
                                            return true;
                                        }
                                    }}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-6">
                                <FormInput
                                    autocomplete="off"
                                    disabled={!canEdit}
                                    form={formFirmwareVersion}
                                    label="Firmware Version"
                                    name="firmwareValue"
                                    registerOptions={{
                                        required: true,
                                    }}
                                />
                            </div>
                        </div>
                    </form>
                </>)}
                {filterType === FilterType.LocationData && (<>
                    <p>
                        Only locations matching this attribute will be included.
                    </p>
                    <div className="row">
                        <div className="col-6">
                            <div className="formItem">
                                <div className="formItem_label">
                                    Attribute
                                </div>
                                <select
                                    className="formItem_select"
                                    disabled={!canEdit}
                                    name="locationAttr"
                                    onChange={onChangeLocationAttr}
                                    value={locationAttr}
                                >
                                    {Object.keys(allLocationAttrVals).map(attr => (
                                        <option value={attr} key={attr}>
                                            {t(`locationAttr.${attr}`)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            {allLocationAttrVals[locationAttr]?.length > 0 && <>
                                <div className="formItem">
                                    <div className="filterModal_attributeValsHeader">
                                        <label className="filterModal_attributeValsHeaderLabel">
                                            <input
                                                type="checkbox"
                                                checked={isSelectedAllValues}
                                                disabled={!canEdit}
                                                onChange={toggleSelectAllValues}
                                            />
                                            Select All
                                        </label>
                                        <span className="filterModal_attributeValsCount">
                                            {Object.keys(locationAttrVals).length} selected
                                        </span>
                                    </div>
                                    <div className="filterModal_attributeVals">
                                        {allLocationAttrVals[locationAttr]?.map((val, i) => (
                                            <div key={i}>
                                                <label className="filterModal_attributeValLabel">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!locationAttrVals[val]}
                                                        disabled={!canEdit}
                                                        onChange={() => onToggleLocationAttrVal(val)}
                                                    />
                                                    {val}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>}
                            {!(allLocationAttrVals[locationAttr]?.length > 0) && (
                                <div>No available values found for this attribute. Perhaps an error occurred loading them from the locations API.</div>
                            )}
                        </div>
                    </div>
                </>)}
                {filterType === FilterType.LastSeen && <>
                    <p>
                        Only include devices that have been last seen before or after a certain date.
                        This can be useful for excluding devices that haven't connected in a long time.
                    </p>
                    <form>
                        <div className="row">
                            <div className="col">
                                <div className="filterModal_lastSeenInputs">
                                    Last seen
                                    <div className="formItem filterModal_lastSeenOperatorInput">
                                        <select
                                            className="formItem_select"
                                            disabled={!canEdit}
                                            value={lastSeenOperator}
                                            onChange={(e) => {
                                                const newOperator = e.target.value;
                                                setState(produce(draft => {
                                                    draft.lastSeenOperator = newOperator as any;
                                                }));
                                            }}
                                        >
                                            <option value=">">after</option>
                                            <option value="<">before</option>
                                        </select>
                                    </div>
                                    <DatePicker 
                                        getPopupContainer={(trig) => trig.parentNode as HTMLElement}
                                        className="filterModal_lastSeenAntPicker"
                                        disabled={!canEdit}
                                        value={lastSeenTimeTimezoned}
                                        allowClear={false}
                                        onChange={onChangeLastSeenDate}
                                    />
                                    <DatePicker.TimePicker
                                        getPopupContainer={(trig) => trig.parentNode as HTMLElement}
                                        className="filterModal_lastSeenAntPicker"
                                        disabled={!canEdit}
                                        value={lastSeenTimeTimezoned}
                                        format={'h:mm a'}
                                        showNow={false}
                                        allowClear={false}
                                        inputReadOnly={true}
                                        onSelect={onChangeLastSeenDate}
                                    />
                                    &nbsp;{hasTimezone && lastSeenTimeDt.offsetNameShort}
                                </div>
                            </div>
                        </div>
                    </form>
                </>}
                {saveError && <ErrorMessage message={saveError} />}
            </ModalBody>
            <ModalFooter>
                <Button
                    variant="outlined" 
                    onClick={onHide}
                    disabled={saveStatus === AsyncStatus.Loading}
                >
                    Close
                </Button>
                <Button
                    style={{marginLeft: "10px"}}
                    onClick={onSave}
                    loading={saveStatus === AsyncStatus.Loading}
                    disabled={!canEdit || saveStatus === AsyncStatus.Success}
                >
                    {saveType === 'save' && saveStatus === AsyncStatus.Loading 
                        ? 'Saving'
                        : 'Save'}
                </Button>
            </ModalFooter>
        </Modal>
    );
}
